/**
 * Spec 171 — the per-column filter's MATCH MODE: Contains (default), Starts
 * with, Ends with.
 *
 * The vocabulary and the value shape live here, in common/, because the
 * component that edits the value (ColumnValuePicker) is in common/ and the
 * component that matches it (reportGridUtils.matchColumnFilter) is in reports/;
 * reports/ may depend on common/, never the reverse. The MATCHING itself stays in
 * reportGridUtils.js next to `forgivingMatch`, so the two matchers cannot drift.
 *
 * THE VALUE SHAPE — the mode travels INSIDE the existing column-filter value,
 * never beside it, so a saved view (`grid.columnFilters`, D1 or the store's
 * `namedViews`) round-trips it with no new store method:
 *
 *   contains:            a bare string (text-only column) or { text, picked }
 *                        (picker column) — EXACTLY what was emitted before this
 *                        spec, so a Contains view is byte-identical to one saved
 *                        by an older build, and an older build reads every view.
 *   starts / ends with:  { text, mode } (text-only) or { text, picked, mode }.
 *
 * Reading is forgiving in the SAFE direction: a bare string, a value with no
 * `mode`, or a `mode` this build does not know all read as Contains. Contains is
 * the widest of the three (every Starts-with or Ends-with match is also a
 * Contains match), so an unreadable mode can only show MORE rows than its author
 * meant, never silently fewer — on a grid whose totals re-sum the visible rows,
 * the opposite failure would move money.
 *
 * Empty text is NO FILTER regardless of mode: `buildColumnFilterValue` answers
 * `undefined`, so an operator chosen before anything is typed never becomes an
 * entry. That matters on the flat grid, whose "Column Filter (N)" count is
 * TanStack's `columnFilters.length` (DataGrid.jsx) — an `{ text: '', mode }`
 * entry would be counted and would scope nothing.
 */

export const MATCH_CONTAINS = 'contains'
export const MATCH_STARTS_WITH = 'startsWith'
export const MATCH_ENDS_WITH = 'endsWith'

/*
 * Words, not symbols (^, $, *): the audience is a law-firm biller, and a native
 * <select> reads its option text aloud. `placeholder` is the input's hint in that
 * mode. Contains keeps "Filter..." EXACTLY (three full stops) — it is an e2e
 * locator (`input[placeholder="Filter..."]`, e2e/reports-grid-audit.spec.js) and
 * the default, so every existing locator keeps finding every input.
 */
export const COLUMN_MATCH_MODES = Object.freeze([
  Object.freeze({ value: MATCH_CONTAINS, label: 'Contains', placeholder: 'Filter...' }),
  Object.freeze({ value: MATCH_STARTS_WITH, label: 'Starts with', placeholder: 'Starts with…' }),
  Object.freeze({ value: MATCH_ENDS_WITH, label: 'Ends with', placeholder: 'Ends with…' }),
])

const ANCHORED = new Set([MATCH_STARTS_WITH, MATCH_ENDS_WITH])

/** Whether `mode` is one of the two anchored modes. Anything else is Contains. */
export const isAnchoredMode = (mode) => ANCHORED.has(mode)

/**
 * The match mode a column-filter value carries. A bare string, `undefined`, a
 * value with no `mode`, and an unknown `mode` are all Contains (see the note at
 * the top for why that is the safe reading).
 */
export const columnFilterMode = (fv) =>
  (fv && typeof fv === 'object' && isAnchoredMode(fv.mode) ? fv.mode : MATCH_CONTAINS)

/** The input's placeholder for a mode — "Filter..." for Contains, as before. */
export const columnFilterPlaceholder = (mode) =>
  (COLUMN_MATCH_MODES.find((m) => m.value === mode) || COLUMN_MATCH_MODES[0]).placeholder

/**
 * The value ColumnValuePicker emits for what is on screen.
 *
 *   text      the typed text (may be '' or whitespace)
 *   picked    the picked values (picker columns only)
 *   mode      the operator; anything unknown is Contains
 *   hasPicker whether the column offers the value picker
 *
 * Contains reproduces the pre-spec-171 emission byte for byte — a text-only
 * column sends `text || undefined` (so a whitespace-only string is still sent,
 * exactly as before), a picker column sends `{ text, picked }` or `undefined`.
 * The anchored modes send `undefined` unless the text has a non-space character
 * or something is picked.
 */
export const buildColumnFilterValue = ({ text, picked, mode, hasPicker }) => {
  // A text-only column has no picks to keep, even if a value carried some (a
  // view saved while the column still offered the picker) — the text input is
  // all it renders, exactly as before this spec.
  const pickedList = hasPicker && Array.isArray(picked) ? picked : []
  const textActive = !!(text && text.trim() !== '')
  if (!isAnchoredMode(mode)) {
    if (!hasPicker) return text || undefined
    return textActive || pickedList.length > 0 ? { text: text || '', picked: pickedList } : undefined
  }
  if (!textActive && pickedList.length === 0) return undefined
  return hasPicker ? { text: text || '', picked: pickedList, mode } : { text, mode }
}
